import { CreateEvidenceUploadResponseSchema, DepositOptionsResponseSchema, MarkPaidResponseSchema, ReleaseTransactionResponseSchema, ReserveDepositOptionResponseSchema, SubmitEvidenceResponseSchema, TransactionDetailSchema, } from '@pf/contracts';
import { CustomerActivityListSchema } from './activity.js';
import { CustomerBalanceSchema } from './balance.js';
import { ActiveDepositResponseSchema, CreateEvidenceUploadRequestSchema, MarkDepositPaidRequestSchema, ReleaseDepositRequestSchema, ReserveDepositRequestSchema, SubmitDepositEvidenceRequestSchema, } from './deposit.js';
import { CustomerLoginRequestSchema, CustomerLogoutResponseSchema, CustomerSessionSchema, } from './session.js';
import { CreateCustomerWithdrawalRequestSchema, CustomerWithdrawalListSchema, CustomerWithdrawalSchema, } from './withdrawal.js';
/**
 * The endpoint manifest — the profile as data rather than prose.
 *
 * Both halves of the default pair read this table: the console's query layer builds its URLs from
 * it and parses every response against the schema named here, and the reference BFF registers its
 * routes from it and validates what it is about to send. That is the whole point of the manifest
 * — a divergence between the UI and the BFF stops being a runtime 404 that someone notices in
 * staging and becomes a type error at the seam.
 *
 * Third-party implementers get the same benefit for free: import this package, iterate
 * {@link CASHIER_ENDPOINTS}, and you have the complete list of what you must serve, with the
 * schema each response has to satisfy.
 */
/**
 * The profile's version.
 *
 * A BFF announces the profile it implements in the `X-Cashier-Profile` response header, and a UI
 * may check it. Bumped only for a breaking change to a schema or a path — additive fields are
 * not breaking, because every schema here is `.strict()` on the *response* side only from the
 * consumer's perspective: a UI that does not know a new field still parses, since the profile
 * requires consumers to tolerate unknown fields at the transport layer if they choose to.
 */
export const CASHIER_PROFILE_VERSION = '1';
/** Header a conforming BFF sets on every profile response, carrying {@link CASHIER_PROFILE_VERSION}. */
export const CASHIER_PROFILE_HEADER = 'x-cashier-profile';
/**
 * Where the profile is mounted by default.
 *
 * Only a default. The UI reads its base path from configuration (`VITE_BFF_BASE`), so a BFF can
 * mount the profile anywhere — `/api/cashier`, a different origin — without either side being
 * patched. What must not move is the shape of the paths *below* the base.
 */
export const DEFAULT_CASHIER_BASE_PATH = '/bff';
/**
 * Every endpoint a Cashier Profile BFF serves.
 *
 * Grouped by the screen that drives it, because that is how an implementer will work through the
 * list — and because it makes the one architectural rule visible: the customer's browser reaches
 * exactly these paths and nothing else. There is no route here that forwards to PaymentFlow's
 * operator API, and there is no route that reveals another customer's data, because every one of
 * them is scoped by the session (SAD P9).
 */
export const CASHIER_ENDPOINTS = {
    // -------------------------------------------------------------------------------------------
    // Session
    // -------------------------------------------------------------------------------------------
    getSession: {
        method: 'GET',
        path: '/auth/session',
        success: 200,
        request: null,
        response: CustomerSessionSchema,
        summary: 'Who the caller is signed in as. 401 with code NO_SESSION when nobody.',
        required: true,
    },
    login: {
        method: 'POST',
        path: '/auth/login',
        success: 200,
        request: CustomerLoginRequestSchema,
        response: CustomerSessionSchema,
        summary: 'Sign a customer in. Optional — a website with its own IdP need not implement it.',
        required: false,
    },
    logout: {
        method: 'POST',
        path: '/auth/logout',
        success: 200,
        request: null,
        response: CustomerLogoutResponseSchema,
        summary: 'End the session. Optional, and paired with login.',
        required: false,
    },
    // -------------------------------------------------------------------------------------------
    // Balances — the website's own ledger. PaymentFlow never sees these.
    // -------------------------------------------------------------------------------------------
    getBalance: {
        method: 'GET',
        path: '/balance',
        success: 200,
        request: null,
        response: CustomerBalanceSchema,
        summary: "The signed-in customer's available, locked and pending-credit balances.",
        required: true,
    },
    // -------------------------------------------------------------------------------------------
    // Deposit — order book → reserve → mark paid → evidence → live status
    // -------------------------------------------------------------------------------------------
    listDepositOptions: {
        method: 'GET',
        path: '/deposit-options',
        success: 200,
        request: null,
        response: DepositOptionsResponseSchema,
        summary: 'The order book: amounts and availability only, never bank details (SAD §5).',
        required: true,
    },
    reserveDeposit: {
        method: 'POST',
        path: '/deposit-options/reserve',
        success: 201,
        request: ReserveDepositRequestSchema,
        response: ReserveDepositOptionResponseSchema,
        summary: 'Atomically claim one withdrawal at an exact amount. 409 OUT_OF_STOCK if none.',
        required: true,
    },
    /*
     * Declared before `/deposits/{id}` so a router that matches in order cannot treat `active` as a
     * transaction id. Fastify prefers a static segment regardless; MSW does not.
     */
    getActiveDeposit: {
        method: 'GET',
        path: '/deposits/active',
        success: 200,
        request: null,
        response: ActiveDepositResponseSchema,
        summary: "The customer's in-flight deposit and where to pay, so a reload can resume it.",
        required: true,
    },
    getDeposit: {
        method: 'GET',
        path: '/deposits/{id}',
        success: 200,
        request: null,
        response: TransactionDetailSchema,
        summary: "A deposit's live state, verification checks and event timeline.",
        required: true,
    },
    markDepositPaid: {
        method: 'POST',
        path: '/deposits/{id}/mark-paid',
        success: 200,
        request: MarkDepositPaidRequestSchema,
        response: MarkPaidResponseSchema,
        summary: 'The transfer has been made: RESERVED → AWAITING_EVIDENCE, evidence deadline starts.',
        required: true,
    },
    releaseDeposit: {
        method: 'POST',
        path: '/deposits/{id}/release',
        success: 200,
        request: ReleaseDepositRequestSchema,
        response: ReleaseTransactionResponseSchema,
        summary: 'Give up a reservation before paying, returning the slot to the pool.',
        required: true,
    },
    createEvidenceUpload: {
        method: 'POST',
        path: '/evidence',
        success: 201,
        request: CreateEvidenceUploadRequestSchema,
        response: CreateEvidenceUploadResponseSchema,
        summary: 'Step one of the two-step slip upload: mint a short-TTL pre-signed URL.',
        required: true,
    },
    submitDepositEvidence: {
        method: 'POST',
        path: '/deposits/{id}/evidence',
        success: 202,
        request: SubmitDepositEvidenceRequestSchema,
        response: SubmitEvidenceResponseSchema,
        summary: 'Step two: submit the slip reference, which triggers verification.',
        required: true,
    },
    // -------------------------------------------------------------------------------------------
    // Withdrawal — request → funds lock → pool position → fulfilment
    // -------------------------------------------------------------------------------------------
    listWithdrawals: {
        method: 'GET',
        path: '/withdrawals',
        success: 200,
        request: null,
        response: CustomerWithdrawalListSchema,
        summary: "The customer's own withdrawal requests, newest first.",
        required: true,
    },
    createWithdrawal: {
        method: 'POST',
        path: '/withdrawals',
        success: 201,
        request: CreateCustomerWithdrawalRequestSchema,
        response: CustomerWithdrawalSchema,
        summary: 'Lock the funds, mint the lock reference, then enter the PaymentFlow pool.',
        required: true,
    },
    cancelWithdrawal: {
        method: 'POST',
        path: '/withdrawals/{id}/cancel',
        success: 200,
        request: null,
        response: CustomerWithdrawalSchema,
        summary: 'Cancel a still-AVAILABLE request and release the funds lock.',
        required: true,
    },
    // -------------------------------------------------------------------------------------------
    // History
    // -------------------------------------------------------------------------------------------
    listActivity: {
        method: 'GET',
        path: '/activity',
        success: 200,
        request: null,
        response: CustomerActivityListSchema,
        summary: 'Deposits and withdrawals in one filterable, paged feed.',
        required: true,
    },
    getActivityWithdrawal: {
        method: 'GET',
        path: '/activity/withdrawals/{id}',
        success: 200,
        request: null,
        response: CustomerWithdrawalSchema,
        summary: "One withdrawal, for the history page's detail panel.",
        required: true,
    },
};
/**
 * Fill a path template's `{}` parameters.
 *
 * Values are percent-encoded, which matters more than it looks: transaction ids come back from
 * PaymentFlow and lock references are minted by the website, and neither side of this profile
 * gets to assume the other's id alphabet.
 */
export function cashierPath(template, params = {}) {
    return template.replace(/\{(\w+)\}/g, (_match, key) => {
        const value = params[key];
        if (value === undefined) {
            throw new Error(`cashierPath: missing parameter "${key}" for template "${template}"`);
        }
        return encodeURIComponent(value);
    });
}
/** Every endpoint a conforming BFF must serve. */
export function requiredCashierEndpoints() {
    return Object.values(CASHIER_ENDPOINTS).filter((endpoint) => endpoint.required);
}
//# sourceMappingURL=endpoints.js.map